
	import { Component, Vue } from 'vue-property-decorator';

	import hostApp from '@/HostApp/main';
	import Notifications from '@/HostApp/ui/components/Dojo2Vue/Notifications.vue';
	import Error404 from '@/HostApp/ui/components/Dojo2Vue/Error404.vue';
	import ContentHeader from '@/HostApp/ui/components/Dojo2Vue/ContentHeader.vue';
	import AppFooter from '@/HostApp/ui/components/Dojo2Vue/AppFooter.vue';
	import SideBar from '@/HostApp/ui/components/Dojo2Vue/SideBar.vue';
	import aps from '@/HostApp/store/ApsModules/ApsModule';
	import analyticsModule from '@/HostApp/services/Analytics/AnalyticsModule';
	import TheModalDialog from '@/HostApp/ui/system/ModalDialog.vue';
	import TheWizardSteps from '@/HostApp/ui/components/wraps/TheWizardSteps.vue';
	import { getNavId } from '@/HostApp/store/ApsModules/NavigationModule';
	import { imagesPath, serverProps } from '@/Common/Environment/const';
	import { DialogEvents } from 'ux1-ui-kit/types/models/ux/dialog';

	enum IntroWizardStates {
		Started = 'Started',
		Finished = 'Finished',
		Skipped = 'Skipped'
	}

	enum RuntimeError {
		Show = 'HOST-SHOW-RUNTIME-ERROR',
		Hide = 'HOST-HIDE-RUNTIME-ERROR'
	}

	@Component({
		components: {
			TheWizardSteps,
			TheModalDialog,
			SideBar,
			AppFooter,
			Notifications,
			Error404,
			ContentHeader
		}
	})
	export default class MainView extends Vue {
		protected showWelcomeWizard = false;
		protected imagesPath = imagesPath;

		protected get isVueWorkspace(): boolean {
			return aps.navigation.isVueWorkspace;
		}

		protected showDialog = false;

		protected get computedStyles(): Partial<CSSStyleDeclaration> {
			return {
				height: '100%',
				width: '100%',
				minWidth: 'auto',
				paddingTop: '0',
				overflow: this.setOverflow()
			};
		}

		protected setOverflow() {
			if (serverProps._vueWorkspace) {
				return 'hidden';
			} else if (this.showDialog) {
				return 'visible';
			} else {
				return 'hidden auto';
			}
		}

		protected created(): void {
			// @ts-ignore
			window.addEventListener(RuntimeError.Show, this.onShowRuntime404Error);
			window.addEventListener('message', this.uiKitEventsHandler);
		}

		protected mounted(): void {
			const { apsContext: context, apsIntro } = serverProps;

			hostApp.apsHost.then(() =>
				window.require(['host/host', 'aps/ready!'], (host) => {
					host({ context }, apsIntro);
					this.showWelcomeWizard = aps.navigation.showWelcomeWizard;
				})
			);
		}

		protected beforeDestroy(): void {
			window.removeEventListener(RuntimeError.Show, this.onShowRuntime404Error);
			window.removeEventListener(RuntimeError.Hide, this.onHideRuntime404Error);
		}

		protected runtimeError404: APS.IError404 | null = null;

		protected onHideRuntime404Error(): void {
			this.runtimeError404 = null;
			window.removeEventListener(RuntimeError.Hide, this.onHideRuntime404Error);
		}

		protected onShowRuntime404Error(): void {
			window.addEventListener(RuntimeError.Hide, this.onHideRuntime404Error);
			// @ts-ignore
			this.runtimeError404 = {
				page404Title: this.$t('Error 404'),
				page404Header: this.$t("Sorry, we can't find the page you are looking for"),
				page404PrimaryText: this.$t('It has been moved, deleted or never existed.')
			};
		}

		protected uiKitEventsHandler(event: MessageEvent): void {
			const { id } = event.data;
			if (!Object.values(DialogEvents).includes(id)) {
				return;
			}

			switch (id) {
				case DialogEvents.openDialog:
					this.showDialog = true;
					break;
				case DialogEvents.closeDialog:
					this.showDialog = false;
					break;
			}
		}

		protected error404ScopedSlots = ['backlink', 'homelink', 'supportlink'];

		protected get secondary404Text(): string {
			return this.$t(
				'You can go back to __backlink__where you were__backlink__, get to the __homelink__home page__homelink__, or contact our __supportlink__support team__supportlink__.'
			);
		}

		//aps legacy methods copied
		protected onErrorLinkClick(item: string): void {
			switch (item) {
				case 'backlink':
					// mock for 404 View
					// @ts-ignore
					aps.navigation.tree.context.push({});
					// @ts-ignore
					aps.apsc.prev();
					break;
				case 'homelink':
					// @ts-ignore
					const [firstItem] = aps.navigation.tree.items;
					const subscriptionId = firstItem.subscriptionId;
					aps.apsc.gotoURL(`/i/${getNavId(firstItem.id)}${subscriptionId ? `/s/${subscriptionId}` : ''}`);
					break;
				default:
					//support link
					// @ts-ignore
					const url = encodeURI(aps.context._supportLinks.supportContactsLink);
					window.open(url, 'blank');
			}
		}

		protected sendGoogleAnalytics(closeState: IntroWizardStates): void {
			analyticsModule.pushHostGAData({
				event: 'NE intro',
				eventCategory: 'NE intro',
				eventAction: closeState
			});
		}

		protected onWizardClosed(): void {
			this.showWelcomeWizard = false;
			aps.navigation.disableWelcomeWizard();
		}

		protected onWizardFinished(): void {
			this.sendGoogleAnalytics(IntroWizardStates.Finished);
		}

		protected onWizardLastStepClosed(): void {
			this.onWizardClosed();
			this.sendGoogleAnalytics(IntroWizardStates.Finished);
		}

		protected onWizardStarted(): void {
			this.sendGoogleAnalytics(IntroWizardStates.Started);
		}

		protected onWizardSkipped(): void {
			this.sendGoogleAnalytics(IntroWizardStates.Skipped);
		}

		protected get isXSClientType(): boolean {
			return serverProps.isXSClientType;
		}

		protected get copyrights(): APS.ICopyrights {
			return serverProps.copyrights;
		}

		protected get error404(): APS.IError404 | null {
			return serverProps.error404;
		}

		protected get breadcrumbs(): APS.IBreadcrumbs {
			return serverProps.breadcrumbs;
		}

		protected get vueWorkspace(): boolean {
			return aps.navigation.isVueWorkspace;
		}

		protected get notifications(): APS.INotifications {
			return serverProps.notifications;
		}
	}
